/**
 * @license
 * Copyright Google Inc. All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */


import {Type, isType} from '../type';
import {global, stringify} from '../util';
import {PlatformReflectionCapabilities} from './platform_reflection_capabilities';
import {GetterFn, MethodFn, SetterFn} from './types';
/**
 * Attention: This regex has to hold even if the code is minified!
 */
export const DELEGATE_CTOR = /^function\s+\S+\(\)\s*{[\s\S]+\.apply\(this,\s*arguments\)/;
export class ReflectionCapabilities implements PlatformReflectionCapabilities {
private _reflect: any;
/**
 * @param {?=} reflect
 */
constructor(reflect?: any) { this._reflect = reflect || global['Reflect']; }
/**
 * @return {?}
 */
isReflectionEnabled(): boolean { return true; }
/**
 * @template T
 * @param {?} t
 * @return {?}
 */
factory<T>(t: Type<T>): (args: any[]) => T { return (...args: any[]) => new t(...args); }
/**
 * \@internal
 * @param {?} paramTypes
 * @param {?} paramAnnotations
 * @return {?}
 */
_zipTypesAndAnnotations(paramTypes: any[], paramAnnotations: any[]): any[][] {
    let /** @type {?} */ result: any[][];

    if (typeof paramTypes === 'undefined') {
      result = new Array(paramAnnotations.length);
    } else {
      result = new Array(paramTypes.length);
    }

    for (let /** @type {?} */ i = 0; i < result.length; i++) {
      // TS outputs Object for parameters without types, while Traceur omits
      // the annotations. For now we preserve the Traceur behavior to aid
      // migration, but this can be revisited.
      if (typeof paramTypes === 'undefined') {
        result[i] = [];
      } else if (paramTypes[i] != Object) {
        result[i] = [paramTypes[i]];
      } else {
        result[i] = [];
      }
      if (paramAnnotations && paramAnnotations[i] != null) {
        result[i] = result[i].concat(paramAnnotations[i]);
      }
    }
    return result;
  }
/**
 * @param {?} type
 * @param {?} parentCtor
 * @return {?}
 */
private _ownParameters(type: Type<any>, parentCtor: any): any[][]|null {
    // If we have no decorators, we only have function.length as metadata.
    // In that case, to detect whether a child class declared an own constructor or not,
    // we need to look inside of that constructor to check whether it is
    // just calling the parent.
    // This also helps to work around for https://github.com/Microsoft/TypeScript/issues/12439
    // that sets 'design:paramtypes' to []
    // if a class inherits from another class but has no ctor declared itself.
    if (DELEGATE_CTOR.exec(type.toString())) {
      return null;
    }

    // Prefer the direct API.
    if (( /** @type {?} */((<any>type))).parameters && ( /** @type {?} */((<any>type))).parameters !== parentCtor.parameters) {
      return ( /** @type {?} */((<any>type))).parameters;
    }

    // API of tsickle for lowering decorators to properties on the class.
    const /** @type {?} */ tsickleCtorParams = ( /** @type {?} */((<any>type))).ctorParameters;
    if (tsickleCtorParams && tsickleCtorParams !== parentCtor.ctorParameters) {
      // Newer tsickle uses a function closure
      // Retain the non-function case for compatibility with older tsickle
      const /** @type {?} */ ctorParameters =
          typeof tsickleCtorParams === 'function' ? tsickleCtorParams() : tsickleCtorParams;
      const /** @type {?} */ paramTypes = ctorParameters.map((ctorParam: any) => ctorParam && ctorParam.type);
      const /** @type {?} */ paramAnnotations = ctorParameters.map(
          (ctorParam: any) =>
              ctorParam && convertTsickleDecoratorIntoMetadata(ctorParam.decorators));
      return this._zipTypesAndAnnotations(paramTypes, paramAnnotations);
    }

    // API for metadata created by invoking the decorators.
    if (this._reflect != null && this._reflect.getOwnMetadata != null) {
      const /** @type {?} */ paramAnnotations = this._reflect.getOwnMetadata('parameters', type);
      const /** @type {?} */ paramTypes = this._reflect.getOwnMetadata('design:paramtypes', type);
      if (paramTypes || paramAnnotations) {
        return this._zipTypesAndAnnotations(paramTypes, paramAnnotations);
      }
    }

    // If a class has no decorators, at least create metadata
    // based on function.length.
    // Note: We know that this is a real constructor as we checked
    // the content of the constructor above.
    return new Array(( /** @type {?} */((<any>type.length)))).fill(undefined);
  }
/**
 * @param {?} type
 * @return {?}
 */
parameters(type: Type<any>): any[][] {
    // Note: only report metadata if we have at least one class decorator
    // to stay in sync with the static reflector.
    if (!isType(type)) {
      return [];
    }
    const /** @type {?} */ parentCtor = getParentCtor(type);
    let /** @type {?} */ parameters = this._ownParameters(type, parentCtor);
    if (!parameters && parentCtor !== Object) {
      parameters = this.parameters(parentCtor);
    }
    return parameters || [];
  }
/**
 * @param {?} typeOrFunc
 * @param {?} parentCtor
 * @return {?}
 */
private _ownAnnotations(typeOrFunc: Type<any>, parentCtor: any): any[]|null {
    // Prefer the direct API.
    if (( /** @type {?} */((<any>typeOrFunc))).annotations && ( /** @type {?} */((<any>typeOrFunc))).annotations !== parentCtor.annotations) {
      let /** @type {?} */ annotations = ( /** @type {?} */((<any>typeOrFunc))).annotations;
      if (typeof annotations === 'function' && annotations.annotations) {
        annotations = annotations.annotations;
      }
      return annotations;
    }

    // API of tsickle for lowering decorators to properties on the class.
    if (( /** @type {?} */((<any>typeOrFunc))).decorators && ( /** @type {?} */((<any>typeOrFunc))).decorators !== parentCtor.decorators) {
      return convertTsickleDecoratorIntoMetadata(( /** @type {?} */((<any>typeOrFunc))).decorators);
    }

    // API for metadata created by invoking the decorators.
    if (this._reflect && this._reflect.getOwnMetadata) {
      return this._reflect.getOwnMetadata('annotations', typeOrFunc);
    }
    return null;
  }
/**
 * @param {?} typeOrFunc
 * @return {?}
 */
annotations(typeOrFunc: Type<any>): any[] {
    if (!isType(typeOrFunc)) {
      return [];
    }
    const /** @type {?} */ parentCtor = getParentCtor(typeOrFunc);
    const /** @type {?} */ ownAnnotations = this._ownAnnotations(typeOrFunc, parentCtor) || [];
    const /** @type {?} */ parentAnnotations = parentCtor !== Object ? this.annotations(parentCtor) : [];
    return parentAnnotations.concat(ownAnnotations);
  }
/**
 * @param {?} typeOrFunc
 * @param {?} parentCtor
 * @return {?}
 */
private _ownPropMetadata(typeOrFunc: any, parentCtor: any): {[key: string]: any[]}|null {
    // Prefer the direct API.
    if (( /** @type {?} */((<any>typeOrFunc))).propMetadata &&
        ( /** @type {?} */((<any>typeOrFunc))).propMetadata !== parentCtor.propMetadata) {
      let /** @type {?} */ propMetadata = ( /** @type {?} */((<any>typeOrFunc))).propMetadata;
      if (typeof propMetadata === 'function' && propMetadata.propMetadata) {
        propMetadata = propMetadata.propMetadata;
      }
      return propMetadata;
    }

    // API of tsickle for lowering decorators to properties on the class.
    if (( /** @type {?} */((<any>typeOrFunc))).propDecorators &&
        ( /** @type {?} */((<any>typeOrFunc))).propDecorators !== parentCtor.propDecorators) {
      const /** @type {?} */ propDecorators = ( /** @type {?} */((<any>typeOrFunc))).propDecorators;
      const /** @type {?} */ propMetadata = /** @type {?} */(( <{[key: string]: any[]}>{}));
      Object.keys(propDecorators).forEach(prop => {
        propMetadata[prop] = convertTsickleDecoratorIntoMetadata(propDecorators[prop]);
      });
      return propMetadata;
    }

    // API for metadata created by invoking the decorators.
    if (this._reflect && this._reflect.getOwnMetadata) {
      return this._reflect.getOwnMetadata('propMetadata', typeOrFunc);
    }
    return null;
  }
/**
 * @param {?} typeOrFunc
 * @return {?}
 */
propMetadata(typeOrFunc: any): {[key: string]: any[]} {
    if (!isType(typeOrFunc)) {
      return {};
    }
    const /** @type {?} */ parentCtor = getParentCtor(typeOrFunc);
    const /** @type {?} */ propMetadata: {[key: string]: any[]} = {};
    if (parentCtor !== Object) {
      const /** @type {?} */ parentPropMetadata = this.propMetadata(parentCtor);
      Object.keys(parentPropMetadata).forEach((propName) => {
        propMetadata[propName] = parentPropMetadata[propName];
      });
    }
    const /** @type {?} */ ownPropMetadata = this._ownPropMetadata(typeOrFunc, parentCtor);
    if (ownPropMetadata) {
      Object.keys(ownPropMetadata).forEach((propName) => {
        const /** @type {?} */ decorators: any[] = [];
        if (propMetadata.hasOwnProperty(propName)) {
          decorators.push(...propMetadata[propName]);
        }
        decorators.push(...ownPropMetadata[propName]);
        propMetadata[propName] = decorators;
      });
    }
    return propMetadata;
  }
/**
 * @param {?} type
 * @param {?} lcProperty
 * @return {?}
 */
hasLifecycleHook(type: any, lcProperty: string): boolean {
    return type instanceof Type && lcProperty in type.prototype;
  }
/**
 * @param {?} name
 * @return {?}
 */
getter(name: string): GetterFn { return /** @type {?} */(( <GetterFn>new Function('o', 'return o.' + name + ';'))); }
/**
 * @param {?} name
 * @return {?}
 */
setter(name: string): SetterFn {
    return /** @type {?} */(( <SetterFn>new Function('o', 'v', 'return o.' + name + ' = v;')));
  }
/**
 * @param {?} name
 * @return {?}
 */
method(name: string): MethodFn {
    const /** @type {?} */ functionBody = `if (!o.${name}) throw new Error('"${name}" is undefined');
        return o.${name}.apply(o, args);`;
    return /** @type {?} */(( <MethodFn>new Function('o', 'args', functionBody)));
  }
/**
 * @param {?} type
 * @return {?}
 */
importUri(type: any): string {
    // StaticSymbol
    if (typeof type === 'object' && type['filePath']) {
      return type['filePath'];
    }
    // Runtime type
    return `./${stringify(type)}`;
  }
/**
 * @param {?} type
 * @return {?}
 */
resourceUri(type: any): string { return `./${stringify(type)}`; }
/**
 * @param {?} name
 * @param {?} moduleUrl
 * @param {?} members
 * @param {?} runtime
 * @return {?}
 */
resolveIdentifier(name: string, moduleUrl: string, members: string[], runtime: any): any {
    return runtime;
  }
/**
 * @param {?} enumIdentifier
 * @param {?} name
 * @return {?}
 */
resolveEnum(enumIdentifier: any, name: string): any { return enumIdentifier[name]; }
}

function ReflectionCapabilities_tsickle_Closure_declarations() {
/** @type {?} */
ReflectionCapabilities.prototype._reflect;
}

/**
 * @param {?} decoratorInvocations
 * @return {?}
 */
function convertTsickleDecoratorIntoMetadata(decoratorInvocations: any[]): any[] {
  if (!decoratorInvocations) {
    return [];
  }
  return decoratorInvocations.map(decoratorInvocation => {
    const /** @type {?} */ decoratorType = decoratorInvocation.type;
    const /** @type {?} */ annotationCls = decoratorType.annotationCls;
    const /** @type {?} */ annotationArgs = decoratorInvocation.args ? decoratorInvocation.args : [];
    return new annotationCls(...annotationArgs);
  });
}
/**
 * @param {?} ctor
 * @return {?}
 */
function getParentCtor(ctor: Function): Type<any> {
  const /** @type {?} */ parentProto = Object.getPrototypeOf(ctor.prototype);
  const /** @type {?} */ parentCtor = parentProto ? parentProto.constructor : null;
  // Note: We always use `Object` as the null value
  // to simplify checking later on.
  return parentCtor || Object;
}



// WEBPACK FOOTER //
// ../../../../packages/core/src/reflection/reflection_capabilities.ts